import { useState, useEffect } from 'react';
import {
  Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, DialogActions,
  FormControl, FormLabel, Input, Textarea, Select, Option, Button, Stack,
  Checkbox, Typography,
} from '@mui/joy';
import {
  ProjectTask, TaskStatus, TaskPriority,
  CreateTaskRequest, UpdateTaskRequest,
  TASK_STATUS_LABELS, TASK_PRIORITY_LABELS,
} from '@/types/project';
import { ShiftType } from '@/types/shiftType';
import { shiftTypeService } from '@/services/shiftTypeService';

interface TaskFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTaskRequest | UpdateTaskRequest) => Promise<void>;
  task?: ProjectTask | null;
  projectId: number;
}

const ALL_TASK_STATUSES: TaskStatus[] = ['not_started', 'in_progress', 'completed', 'blocked'];
const ALL_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high'];

export default function TaskForm({ open, onClose, onSubmit, task, projectId }: TaskFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaskStatus>('not_started');
  const [isPrivate, setIsPrivate] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [shiftTypeId, setShiftTypeId] = useState<number | ''>('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    shiftTypeService.getAllShiftTypes().then(setShiftTypes).catch(() => {});
  }, []);

  useEffect(() => {
    if (task) {
      setName(task.name);
      setDescription(task.description || '');
      setStatus(task.status);
      setIsPrivate(task.is_private ?? false);
      setDueDate(task.due_date || '');
      setPriority(task.priority);
      setShiftTypeId(task.shift_type_id ?? '');
      setEstimatedHours(task.estimated_hours?.toString() || '');
    } else {
      setName('');
      setDescription('');
      setStatus('not_started');
      setIsPrivate(false);
      setDueDate('');
      setPriority('medium');
      setShiftTypeId('');
      setEstimatedHours('');
    }
  }, [task, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (task) {
        const data: UpdateTaskRequest = {
          name, description: description || undefined, status, is_private: isPrivate, priority,
          due_date: dueDate || null,
          shift_type_id: shiftTypeId === '' ? null : shiftTypeId,
          estimated_hours: estimatedHours ? parseFloat(estimatedHours) : null,
        };
        await onSubmit(data);
      } else {
        const data: CreateTaskRequest = {
          project_id: projectId, name, description: description || undefined,
          status, is_private: isPrivate, priority, due_date: dueDate || undefined,
          shift_type_id: shiftTypeId === '' ? undefined : shiftTypeId as number,
          estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
        };
        await onSubmit(data);
      }
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 500, width: '100%' }}>
        <ModalClose />
        <DialogTitle>{task ? 'Edit Task' : 'New Task'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Name</FormLabel>
                <Input value={name} onChange={e => setName(e.target.value)} autoFocus />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea minRows={2} value={description} onChange={e => setDescription(e.target.value)} />
              </FormControl>
              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Status</FormLabel>
                  <Select value={status} onChange={(_, v) => v && setStatus(v)}>
                    {ALL_TASK_STATUSES.map(s => (
                      <Option key={s} value={s}>{TASK_STATUS_LABELS[s]}</Option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Priority</FormLabel>
                  <Select value={priority} onChange={(_, v) => v && setPriority(v)}>
                    {ALL_PRIORITIES.map(p => (
                      <Option key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</Option>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <FormControl>
                <FormLabel>Due Date</FormLabel>
                <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Required Shift Type</FormLabel>
                <Select
                  value={shiftTypeId}
                  onChange={(_, v) => setShiftTypeId(v ?? '')}
                  placeholder="None (any employee)"
                >
                  <Option value="">None</Option>
                  {shiftTypes.map(st => (
                    <Option key={st.id} value={st.id}>{st.name}</Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Estimated Hours</FormLabel>
                <Input type="number" slotProps={{ input: { min: 0, step: 0.5 } }} value={estimatedHours} onChange={e => setEstimatedHours(e.target.value)} />
              </FormControl>
              <Checkbox
                checked={isPrivate}
                onChange={e => setIsPrivate(e.target.checked)}
                label={
                  <Stack>
                    <Typography level="body-sm">Private</Typography>
                    <Typography level="body-xs" color="neutral">Only visible to you and assigned members</Typography>
                  </Stack>
                }
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={loading} disabled={!name.trim()}>
              {task ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </ModalDialog>
    </Modal>
  );
}
